import NumberPicker from 'src/common/components/NumberPicker/NumberPicker';

import I18n from '~/src/common/services/I18n';
import { useNiceModal } from '~/src/common/services/ModalsManager';
import Tracker from '~/src/common/services/Tracker';
import { useGetCart } from '~/src/queries/api-ecom/generated/api-ecom';
import ArticleItem from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/components/ArticleItem';
import { useRecipeScreenAnalyticsContext } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/context';
import { RecipeIngredientProductArticle } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/types';

import * as S from './layout';

interface Props {
  ingredient: RecipeIngredientProductArticle;
  originalProduct?: RecipeIngredientProductArticle;
  recipeId: string;
  onReplace: (originalArticleID: string, replacedArticleID: Nullable<string>) => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
}

const IngredientPanelItem = ({
  ingredient,
  recipeId,
  onReplace,
  onUpdateQuantity,
  originalProduct,
}: Props) => {
  const { categoryName, subcategoryName } = useRecipeScreenAnalyticsContext();

  const { availableQuantity, quantity: requestedQuantity } = ingredient;

  const handleQuantityChange = (value: number) => {
    Tracker.sendEvent('click ingredient picker', {
      'add or remove status': value > ingredient.quantity ? 'product added' : 'product removed',
      'product id': ingredient.id,
      'product name': ingredient.name,
      'product quantity': value,
      'product event source': 'recipe ingredients list',
      'product category': categoryName ?? '',
      'product subcategory': subcategoryName,
    });

    onUpdateQuantity(ingredient.id, value);
  };

  const { show: showSubstitutesModal } = useNiceModal('ingredients-substitute-modal');

  const handleReplaceArticle = () => {
    void showSubstitutesModal({
      product: ingredient,
      originalProduct,
      onReplace,
      categoryName,
      subcategoryName,
    });
  };

  const { data: cart } = useGetCart();
  const cartQuantity =
    cart?.products?.find(p => p.id === ingredient.id)?.quotation2.count.quantity ?? 0;

  const isAvailable = availableQuantity - cartQuantity >= ingredient.packSize;
  const maximumValue = isAvailable ? availableQuantity - cartQuantity : 0;
  const quantity = Math.min(requestedQuantity, maximumValue);

  return (
    <S.IngredientPanelItemContainer>
      <ArticleItem
        article={ingredient}
        onReplace={handleReplaceArticle}
        originalArticle={originalProduct}
        source={{
          type: 'recipe ingredients list',
          detail: {
            recipeId,
          },
        }}
      />
      {isAvailable ? (
        <NumberPicker
          value={quantity}
          maximumValue={maximumValue}
          onChange={handleQuantityChange}
          step={ingredient.packSize}
          maximumValueReachedText={I18n.t('common.picker-sold-out-tooltip')}
        />
      ) : (
        <S.UnavailableProduct>{I18n.t('recipe-screen.unavailable')}</S.UnavailableProduct>
      )}
    </S.IngredientPanelItemContainer>
  );
};

export default IngredientPanelItem;
